import { debounce } from '@mui/material';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import DataHeader from 'components/DataHeader';
import DownloadExcelButton from 'components/form/DownloadExcelButton';
import { translate } from 'components/translation/translation';
import { Lens } from 'icone';
import React, { useCallback, useEffect, /*useMemo,*/ useRef, useState } from 'react';
import { ButtonToolbar, FormControl, InputGroup } from 'react-bootstrap';
import AG_GRID_LOCALE_IT from 'components/ag-grid/locale-it';
import { columnCentered } from 'lib/helpers/agGridHelpers';
import PeriodicReportingStatusCell from './PeriodicReportingStatusCell';
import PeriodicReportingInfoCell from './PeriodicReportingInfoCell';
import CustomPagination from 'components/atomic/organisms/ProjectReporting/CustomPagination';
import {
	downloadInvoicesOrTimesheetsByProjectIdAndStatementId,
	getInvoicesByProjectIdAndStatementId,
	pickAllInvoicesByProjectIdAndStatementId,
	TotalType,
	unpickAllInvoicesByProjectIdAndStatementId,
} from 'lib/api_ts/periodicReportingApi';
import { useLocation } from 'react-router-dom-v5-compat';
import { formatToDecimals } from 'lib/helpers/formatting';
import { PeriodicReportingType } from '../PeriodicReporting';
import CustomTooltip from 'components/ag-grid/CustomTooltip';

export type ExternalCostType = {
	id?: number;
	beneficiaryName: string;
	number: string;
	rpCode: string;
	supplierName: string;
	noticeExpenseItemName: string;
	expenseItemName: string;
	amount: number;
	toSettle: number;
};

interface PeriodicReportingExternalCostsProps {
	data: PeriodicReportingType;
	isCoordinator?: boolean;
	isEnabledToView?: boolean;
	amounts: TotalType;
	refreshAmounts: () => void;
	state: 'DRAFT' | 'VALIDATED';
}

const PeriodicReportingExternalCosts: React.FC<PeriodicReportingExternalCostsProps> = ({
	data,
	isCoordinator,
	isEnabledToView,
	amounts,
	refreshAmounts,
	state,
}) => {
	const { pathname } = useLocation();
	const projectId = pathname.split('/')[2];
	const [filterInputValue, setFilterInputValue] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const localeText = AG_GRID_LOCALE_IT;

	const gridRef = useRef(null);

	const [rowData, setRowData] = useState<ExternalCostType[]>([]);
	const [loading, setLoading] = useState(true);
	const [count, setCount] = useState<number>();
	const [currentPage, setCurrentPage] = useState(0);
	const [perPage, setPerPage] = useState(100);
	const [defaultPerPage, setDefaultPerPage] = useState(100);

	const [pickLoading, setPickLoading] = useState(false);

	const fetchData = useCallback(async () => {
		try {
			const { data: invoices, meta } = await getInvoicesByProjectIdAndStatementId(projectId, data.id, {
				currentPage: currentPage + 1,
				perPage,
				searchTerm,
			});
			const formattedData = invoices.map((item) => {
				return {
					id: item.id,
					beneficiaryName: item.beneficiaryName,
					number: item.number,
					rpCode: item.rpCode,
					supplierName: item.supplierName,
					noticeExpenseItemName: item.noticeExpenseItemName,
					expenseItemName: item.expenseItemName,
					amount: item.amount,
					toSettle: item.toSettle,
					checked: item.checked,
					streamId: item.streamId,
				};
			});
			setRowData(formattedData);
			setSelectedIds(formattedData.filter((row) => row.checked === 1).map((row) => row.id));
			setCount(meta.total);
			if (meta.perPage !== perPage) {
				setPerPage(meta.perPage);
				setDefaultPerPage(meta.perPage);
			}
		} catch (error) {
			console.log('error', error);
		} finally {
			setLoading(false);
		}
	}, [currentPage, perPage, searchTerm, projectId, data.id]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const headerElements = [
		{
			name: <span className="text-nowrap">{translate('common.externalcosts.totalEligibles', 'Importo Rendicontato Eleggibile')}</span>,
			value: formatToDecimals(amounts?.totalEligibles) + ' €',
		},
		{
			name: (
				<span className="text-nowrap">
					{translate('common.externalcosts.totalEligibles', 'Importo Rendicontato Eleggibile')} -{' '}
					{translate('common.externalcosts.totalEligiblesToValidate', 'Elementi da validare')}
				</span>
			),
			value: formatToDecimals(amounts?.totalToSettle) + ' €',
		},
	];

	const columnDefs = [
		...((isCoordinator || isEnabledToView) && state === 'DRAFT'
			? [
					{
						headerName: '',
						maxWidth: 50,
						resizable: false,
						headerCheckboxSelection: true,
						checkboxSelection: true,
						cellStyle: () => {
							//per disabilitare il click sulla riga in loading
							return loading || pickLoading ? { pointerEvents: 'none', opacity: '0.4' } : null;
						},
					},
				]
			: []),
		{
			headerName: 'Beneficiario',
			field: 'beneficiaryName',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Nr. Fattura',
			field: 'number',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'ID Fattura',
			field: 'rpCode',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Fornitore',
			field: 'supplierName',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'WP/Stream',
			field: 'streamId',
			filter: 'agTextColumnFilter',
			...columnCentered(),
		},
		{
			headerName: 'Voce di spesa',
			// field: 'expenseItemName',
			field: 'noticeExpenseItemName',
			filter: 'agTextColumnFilter',
			tooltipField: 'name',
			tooltipComponent: CustomTooltip,
		},
		{
			headerName: 'Importo rendicontabile €',
			field: 'amount',
			filter: 'agTextColumnFilter',
			type: 'rightAligned',
			resizable: true,
			valueGetter: (params: { data: { amount: number } }) => {
				return params.data.amount ? formatToDecimals(params.data.amount) : 0;
			},
		},
		{
			headerName: 'Status',
			field: 'toSettle',
			cellRenderer: PeriodicReportingStatusCell,
			minWidth: 90,
			maxWidth: 90,
			...columnCentered(),
		},
		{
			headerName: 'Info',
			field: 'toSettle',
			cellRenderer: PeriodicReportingInfoCell,
			cellRendererParams: {
				selectedIds,
				statementId: data.id,
				fetchData,
				isCoordinator,
				isEnabledToView,
			},
			minWidth: 90,
			maxWidth: 90,
			...columnCentered(),
		},
	];

	const defaultColDef = {
		resizable: false,
		wrapText: true,
		autoHeight: true,
		flex: 1,
		suppressMovable: true,
		cellStyle: {
			padding: '0 1rem',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		},
	};

	const debouncedSetSearchTermRef = useRef(
		debounce((value) => {
			setCurrentPage(0);
			setPerPage(defaultPerPage);
			if (gridRef.current) {
				gridRef.current.api.paginationGoToPage(0);
				gridRef.current.api.paginationSetPageSize(defaultPerPage);
			}
			setSearchTerm(value); // Solo l'aggiornamento finale dopo debounce
		}, 500),
	);
	const onFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setFilterInputValue(value);
	};

	useEffect(() => {
		debouncedSetSearchTermRef.current(filterInputValue);
	}, [filterInputValue]);

	const handleData = (params: {
		api: {
			forEachNode: (
				arg0: (node: { setSelected: (arg0: boolean, arg1: boolean, arg2: boolean) => void; data: { checked: number } }) => void,
			) => void;
		};
	}) => {
		params.api.forEachNode((node) => {
			node.setSelected(node.data.checked === 1, false, true);
		});
	};

	const pickAll = async (invoiceIds: number[]) => {
		try {
			await pickAllInvoicesByProjectIdAndStatementId(projectId, data.id, invoiceIds);
		} catch (error) {
			console.log('error', error);
		}
	};
	const unpickAll = async (invoiceIds: number[]) => {
		try {
			await unpickAllInvoicesByProjectIdAndStatementId(projectId, data.id, invoiceIds);
		} catch (error) {
			console.log('error', error);
		}
	};
	return (
		<>
			<div className="d-flex justify-content-between">
				<DataHeader
					customStyle={{
						margin: 0,
						paddingTop: '5px',
						paddingBottom: 0,
						marginLeft: '15px',
					}}
					data={headerElements}
				/>
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						alignItems: 'center',
					}}
				>
					<ButtonToolbar className="justify-content-end">
						<DownloadExcelButton
							apiDownload={downloadInvoicesOrTimesheetsByProjectIdAndStatementId}
							apiProps={{ projectId, statementId: data.id, docType: 'invoices' }}
							label={translate('common.downloadreport', 'Scarica report')}
						/>

						<InputGroup className="search-table align-items-center mt-md-0 ms-1" style={{ width: '300px' }}>
							<FormControl
								type="text"
								className="filter-text-box"
								placeholder={translate('common.search', 'Cerca') + '...'}
								value={filterInputValue}
								onChange={onFilterChange}
							/>
							<div style={{ width: '40px', textAlign: 'center' }}>
								<Lens className="lens" />
							</div>
						</InputGroup>
					</ButtonToolbar>
				</div>
			</div>
			<div className="ag-theme-alpine list-table relative">
				<AgGridReact
					ref={gridRef}
					rowData={rowData}
					columnDefs={[...columnDefs]}
					defaultColDef={defaultColDef}
					domLayout="autoHeight"
					tooltipShowDelay={0}
					tooltipHideDelay={2000}
					tooltipMouseTrack={true}
					headerHeight={50}
					rowHeight={50}
					suppressCellFocus={true}
					localeText={localeText}
					rowSelection="multiple"
					rowMultiSelectWithClick={true}
					onFirstDataRendered={handleData}
					onGridReady={handleData}
					suppressRowClickSelection={true}
					onSelectionChanged={(event) => {
						(async () => {
							setPickLoading(true);
							const selectedRows = event.api.getSelectedRows();
							const idsToPick = selectedRows.map((row: ExternalCostType) => row.id);

							setSelectedIds(idsToPick);
							await pickAll(idsToPick);

							const allRows = event.api.getRenderedNodes().map((node) => node.data);
							const idsToUnpick = allRows.filter((row) => !idsToPick.includes(row.id)).map((row) => row.id);

							await unpickAll(idsToUnpick);
							setPickLoading(false);
							refreshAmounts();
						})();
					}}
					onPaginationChanged={(event) => {
						if (event.newData === undefined) return;
						handleData(event);
					}}
				/>
			</div>
			<CustomPagination
				count={count || 100}
				loading={loading}
				currentPage={currentPage || 0}
				setCurrentPage={setCurrentPage}
				perPage={perPage || 100}
				setPerPage={setPerPage}
				gridRef={gridRef}
			/>
		</>
	);
};

export default PeriodicReportingExternalCosts;
